import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  CheckCircle, Package, Truck, Clock, MapPin, Phone, 
  Mail, ChevronLeft, Store, Loader2 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { supermarket } from '@/lib/api';

const SupermarketOrderPage = () => {
  const { orderId } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const isTurkish = i18n.language === 'tr';

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const res = await supermarket.getOrder(orderId);
      setOrder(res.data);
    } catch (error) {
      console.error('Failed to fetch order:', error);
    } finally {
      setLoading(false);
    }
  };

  const statusConfig = {
    pending: { 
      label: isTurkish ? 'Beklemede' : 'Pending', 
      color: 'bg-yellow-100 text-yellow-800',
      icon: Clock
    },
    confirmed: { 
      label: isTurkish ? 'Onaylandı' : 'Confirmed', 
      color: 'bg-blue-100 text-blue-800',
      icon: CheckCircle
    },
    preparing: { 
      label: isTurkish ? 'Hazırlanıyor' : 'Preparing', 
      color: 'bg-purple-100 text-purple-800',
      icon: Package
    },
    ready: { 
      label: isTurkish ? 'Hazır' : 'Ready', 
      color: 'bg-emerald-100 text-emerald-800',
      icon: Package
    },
    delivered: { 
      label: isTurkish ? 'Teslim Edildi' : 'Delivered', 
      color: 'bg-green-100 text-green-800',
      icon: CheckCircle
    },
    cancelled: { 
      label: isTurkish ? 'İptal Edildi' : 'Cancelled', 
      color: 'bg-red-100 text-red-800',
      icon: Clock
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 max-w-2xl">
          <Skeleton className="h-8 w-48 mb-6" />
          <Skeleton className="h-64 w-full rounded-xl mb-4" />
          <Skeleton className="h-48 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">
            {isTurkish ? 'Sipariş bulunamadı' : 'Order not found'}
          </h2>
          <Button onClick={() => navigate('/supermarkets')} variant="outline">
            <ChevronLeft className="w-4 h-4 mr-2" />
            {isTurkish ? 'Süpermarketlere Dön' : 'Back to Supermarkets'}
          </Button>
        </div>
      </div>
    );
  }

  const status = statusConfig[order.status] || statusConfig.pending;
  const StatusIcon = status.icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-background" data-testid="order-confirmation-page">
      {/* Success Header */}
      <section className="py-12 text-center">
        <div className="container mx-auto px-4">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mb-2">
            {isTurkish ? 'Siparişiniz Alındı!' : 'Order Confirmed!'}
          </h1>
          <p className="text-muted-foreground">
            {isTurkish ? 'Sipariş numaranız' : 'Your order number'}:
            <span className="font-mono font-semibold ml-2">#{order.id.slice(0, 8).toUpperCase()}</span>
          </p>
        </div>
      </section>

      {/* Order Details */}
      <section className="pb-12">
        <div className="container mx-auto px-4 max-w-2xl">
          {/* Status Card */}
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  {isTurkish ? 'Sipariş Durumu' : 'Order Status'}
                </CardTitle>
                <Badge className={status.color}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {status.label}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {/* Progress Steps */}
              <div className="flex justify-between items-center">
                {['pending', 'confirmed', 'preparing', 'delivered'].map((step, idx) => {
                  const stepStatus = statusConfig[step];
                  const isActive = ['pending', 'confirmed', 'preparing', 'ready', 'delivered'].indexOf(order.status) >= idx;
                  const StepIcon = stepStatus.icon;
                  
                  return (
                    <div key={step} className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isActive ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-400'
                      }`}>
                        <StepIcon className="w-5 h-5" />
                      </div>
                      <span className={`text-xs mt-1 ${isActive ? 'text-emerald-600 font-medium' : 'text-gray-400'}`}>
                        {stepStatus.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Order Items */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="w-5 h-5" />
                {isTurkish ? 'Sipariş Detayları' : 'Order Details'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {order.items?.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <span className="font-bold text-emerald-600">{item.brand?.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="font-medium text-sm">{item.name}</p>
                      <p className="text-xs text-muted-foreground">{item.brand} × {item.quantity}</p>
                    </div>
                  </div>
                  <span className="font-semibold">₺{item.total?.toFixed(2)}</span>
                </div>
              ))}
              
              <Separator />
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{isTurkish ? 'Ara Toplam' : 'Subtotal'}</span>
                  <span>₺{order.subtotal?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>{isTurkish ? 'Teslimat' : 'Delivery'}</span>
                  <span className={order.delivery_fee === 0 ? 'text-green-600' : ''}>
                    {order.delivery_fee === 0 
                      ? (isTurkish ? 'Ücretsiz' : 'Free') 
                      : `₺${order.delivery_fee?.toFixed(2)}`}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between font-semibold text-lg">
                  <span>{isTurkish ? 'Toplam' : 'Total'}</span>
                  <span className="text-emerald-600">₺{order.total?.toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Delivery Info */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Truck className="w-5 h-5" />
                {isTurkish ? 'Teslimat Bilgileri' : 'Delivery Information'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">{order.customer_name}</p>
                  <p className="text-sm text-muted-foreground">{order.delivery_address}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-muted-foreground" />
                <span>{order.customer_phone}</span>
              </div>
              {order.customer_email && (
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground" />
                  <span>{order.customer_email}</span>
                </div>
              )}
              {order.notes && (
                <div className="mt-3 p-3 bg-secondary/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    <strong>{isTurkish ? 'Not:' : 'Note:'}</strong> {order.notes}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => navigate('/supermarkets')}
            >
              <Store className="w-4 h-4 mr-2" />
              {isTurkish ? 'Alışverişe Devam Et' : 'Continue Shopping'}
            </Button>
            <Link to="/" className="flex-1">
              <Button className="w-full">
                {isTurkish ? 'Ana Sayfaya Dön' : 'Back to Home'}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SupermarketOrderPage;
